import { useMsal } from '@azure/msal-react'
import {
  faChevronLeft,
  faDownload,
  faExpand,
  faImage,
  faPaperclip,
  faPencil,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RequestChangesModal } from '../../components/RequestChangesModal'
import { useSessionInfo } from '../../contexts/SessionContext'
import {
  getDesignRequestById,
  sendAdminMessage,
  updateAdImageUrl,
} from '../../data/admin.service'
import {
  approveDesignRequest,
  fetchDesignRequestById,
  requestDesignChanges,
  sendDesignRequestMessage,
} from '../../data/designRequest.service'
import {
  DesignRequest,
  DesignRequestMessage,
  FileWithUrl,
} from '../../models/DesignRequest'
import { AdStatuses, COLORS } from '../../utils/constants'
import { AdStatusTooltip } from '../../components/dashboard/AdStatusTooltip'
import { convertServerDateToLocal } from '../../utils/dateUtils'
import { downloadFiles, uploadBlobFromFile } from '../../utils/fileUtils'
import { convertHeicToJpeg } from '../../utils/imageUtils'
import { useLocalStorage } from '@mantine/hooks'

interface DesignRequestDetailsPageProps {
  isAdmin?: boolean
}

export default function DesignRequestDetailsPage({
  isAdmin = false,
}: DesignRequestDetailsPageProps) {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [isHostMode] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })
  const [designRequest, setDesignRequest] = useState<DesignRequest | null>(null)
  const [adminDesignRequest, setAdminDesignRequest] =
    useState<DesignRequest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [messageInput, setMessageInput] = useState('')
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [selectedAd, setSelectedAd] = useState<string | null>(null)
  const [requestingChanges, setRequestingChanges] = useState(false)
  const [showChangesModal, setShowChangesModal] = useState(false)
  const [isUploadingMessage, setIsUploadingMessage] = useState(false)
  const [isUploadingDesign, setIsUploadingDesign] = useState(false)
  const [adFile, setAdFile] = useState<FileWithUrl | null>(null)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  useEffect(() => {
    const fetchRequest = async () => {
      if (!id) return
      setIsLoading(true)
      try {
        if (isAdmin) {
          const request = await getDesignRequestById(instance, parseInt(id))
          setAdminDesignRequest(request)
        } else {
          const request = await fetchDesignRequestById(instance, parseInt(id))
          setDesignRequest(request)
        }
      } catch (error) {
        console.error('Error fetching design request:', error)
        notifications.show({
          title: 'Error loading design request',
          message: 'Unable to load design request. Please try again.',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchRequest()
  }, [instance, id, isAdmin])

  const handleApproveDesign = async () => {
    if (!id) return

    try {
      setIsApproving(true)
      await approveDesignRequest(instance, parseInt(id))

      setDesignRequest((prev) =>
        prev
          ? {
              ...prev,
              status: AdStatuses.Running,
            }
          : null,
      )

      notifications.show({
        title: 'Success',
        message: 'Design has been approved and is now running',
        color: 'green',
      })
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to approve design',
        color: 'red',
      })
    } finally {
      setIsApproving(false)
    }
  }

  const handleFileChange = async (files: File[]) => {
    if (files) {
      try {
        // Convert HEIC files to JPEG if needed
        const processedFiles = await Promise.all(
          files.map((file) => convertHeicToJpeg(file)),
        )
        setAttachedFiles((prev) => [...prev, ...processedFiles])
      } catch (error) {
        let errorMessage = 'Failed to process image files. Please try again.'

        if (error instanceof Error) {
          if (error.message.includes('Failed to convert HEIC')) {
            errorMessage =
              'Failed to convert HEIC image. Please try a different image format.'
          } else {
            errorMessage = error.message
          }
        }

        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
          autoClose: 5000,
        })
      }
    }
  }

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSendMessage = async () => {
    if (!id) return

    try {
      setIsUploadingMessage(true)
      const fileUrls: FileWithUrl[] = []

      // Upload each file to blob storage
      for (const file of attachedFiles) {
        try {
          const url = await uploadBlobFromFile('advertisements', file, instance)
          fileUrls.push({
            url,
            fileName: file.name,
          })
        } catch (uploadError) {
          let errorMessage = `Failed to upload ${file.name}. Please try again.`

          if (uploadError instanceof Error) {
            if (uploadError.message.includes('Failed to upload file')) {
              errorMessage = `Network error uploading ${file.name}. Please check your connection.`
            } else {
              errorMessage = uploadError.message
            }
          }

          notifications.show({
            title: 'Upload Failed',
            message: errorMessage,
            color: 'red',
            autoClose: 5000,
          })
          return
        }
      }

      if (isAdmin) {
        await sendAdminMessage(instance, parseInt(id), messageInput, fileUrls)
        // Refresh the design request to get the updated messages
        const request = await getDesignRequestById(instance, parseInt(id))
        setAdminDesignRequest(request)
      } else {
        await sendDesignRequestMessage(instance, parseInt(id), {
          message: messageInput,
          files: fileUrls,
        })
        // Refresh the design request to get the updated messages
        const request = await fetchDesignRequestById(instance, parseInt(id))
        setDesignRequest(request)
      }

      // Clear the message input and attached files
      setMessageInput('')
      setAttachedFiles([])

      notifications.show({
        title: 'Message Sent',
        message: 'We will review your message and get back to you soon.',
        color: 'green',
      })
    } catch (error) {
      let errorMessage = 'Failed to send message. Please try again.'

      if (error instanceof Error) {
        if (
          error.message.includes('network') ||
          error.message.includes('connection')
        ) {
          errorMessage =
            'Network error. Please check your connection and try again.'
        } else {
          errorMessage = error.message
        }
      }

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      })
    } finally {
      setIsUploadingMessage(false)
    }
  }

  const handleRequestChanges = async (changes: string) => {
    if (!id) return

    try {
      setRequestingChanges(true)
      const response = await requestDesignChanges(
        instance,
        parseInt(id),
        changes,
      )

      setDesignRequest((prev) =>
        prev
          ? {
              ...prev,
              status: AdStatuses.ChangesRequested,
              messages: [...prev.messages, response],
            }
          : null,
      )

      notifications.show({
        title: 'Changes Requested',
        message: 'We will review your request and get back to you soon.',
        color: 'green',
      })
      setShowChangesModal(false)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to request changes',
        color: 'red',
      })
    } finally {
      setRequestingChanges(false)
    }
  }

  const handleFileUpload = async (file: File) => {
    setIsUploadingDesign(true)
    try {
      // Convert HEIC to JPEG if needed
      const processedFile = await convertHeicToJpeg(file)

      const url = await uploadBlobFromFile(
        'advertisements',
        processedFile,
        instance,
      )
      setAdFile({ fileName: processedFile.name, url })
      setUploadedUrl(url)
    } catch (ex) {
      let errorMessage = 'The file failed to upload. Please try again.'

      if (ex instanceof Error) {
        if (ex.message.includes('Failed to convert HEIC')) {
          errorMessage =
            'Failed to convert HEIC image. Please try a different image format.'
        } else if (ex.message.includes('Failed to upload file')) {
          errorMessage =
            'Network error. Please check your connection and try again.'
        } else {
          errorMessage = ex.message
        }
      }

      notifications.show({
        title: 'Upload Failed',
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      })
    } finally {
      setIsUploadingDesign(false)
    }
  }

  const handleUpdateImageUrl = async () => {
    if (!uploadedUrl || !id) return

    setIsSending(true)
    try {
      await updateAdImageUrl(parseInt(id), uploadedUrl, instance)

      setAdminDesignRequest((prev) =>
        prev
          ? {
              ...prev,
              advertisementUrl: uploadedUrl,
              status: AdStatuses.PendingAdvertiserApproval,
            }
          : null,
      )

      // Clear upload state
      setAdFile(null)
      setUploadedUrl(null)

      notifications.show({
        title: 'Success',
        color: 'green',
        message: 'Image URL updated successfully.',
        autoClose: true,
      })
    } catch (ex) {
      notifications.show({
        title: 'An unknown error occurred',
        message: 'Unable to update image URL. Please try again.',
        autoClose: false,
      })
    } finally {
      setIsSending(false)
    }
  }

  if (isLoading || (!designRequest && !adminDesignRequest)) {
    return (
      <Stack gap='xl'>
        {!isAdmin && (
          <Group
            justify='space-between'
            align='center'
            h={{ base: 'auto', sm: 70 }}
            px={24}
            py={{ base: 16, sm: 0 }}
            style={{
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Group gap='md'>
              <Skeleton height={32} width={32} radius='md' />
              <Skeleton height={32} width={200} />
            </Group>
          </Group>
        )}

        <Stack gap='xl' px={24} pb={24}>
          <Paper withBorder p='md' radius='md'>
            <Stack gap='xl'>
              <Group align='flex-start' gap='xl'>
                <Skeleton height={175} width={175} radius='md' />
                <Stack gap={4} style={{ flex: 1 }}>
                  <Group gap='xs'>
                    <Skeleton height={24} width={200} />
                    <Skeleton height={24} width={100} radius='xl' />
                  </Group>
                  <Skeleton height={20} width={150} />
                  <Skeleton height={20} width={180} />
                  <Skeleton height={20} width={120} />
                  <Skeleton height={20} width={100} />
                  <Group>
                    <Skeleton height={20} width={80} />
                    <Skeleton height={24} width={100} radius='md' />
                  </Group>
                </Stack>
              </Group>
            </Stack>
          </Paper>

          <Stack gap='xs'>
            <Skeleton height={24} width={100} />
            <Skeleton height={120} radius='md' />
            <Skeleton height={120} radius='md' />
            <Skeleton height={120} radius='md' />
          </Stack>
        </Stack>
      </Stack>
    )
  }

  const request = isAdmin ? adminDesignRequest : designRequest
  if (!request) return null

  const getStatusText = (status: string) => {
    switch (status) {
      case AdStatuses.PendingApproval:
        return 'Pending Host Approval'
      case AdStatuses.Running:
        return 'Running'
      case AdStatuses.Upcoming:
        return 'Upcoming'
      case 'Expired':
        return 'Expired'
      case AdStatuses.Denied:
        return 'Denied'
      case 'Archived':
        return 'Archived'
      case AdStatuses.Disabled:
        return 'Disabled'
      case AdStatuses.PendingDesign:
        return 'Pending Design'
      case AdStatuses.PendingAdvertiserApproval:
        return 'Pending Approval'
      case AdStatuses.ChangesRequested:
        return 'Changes Requested'
      default:
        return 'Advertisements'
    }
  }

  // Helper function to get status color
  const getStatusColor = (status?: string) => {
    if (!status) return COLORS.LightGreen
    if (status === AdStatuses.PendingAdvertiserApproval)
      return COLORS.LightGreen
    if (status === AdStatuses.ChangesRequested) return COLORS.HiddenGemzRed
    return COLORS.LightGreen
  }

  return (
    <Stack gap='xl' h={'100%'}>
      <Group
        justify='space-between'
        align='center'
        h={{ base: 'auto', sm: 70 }}
        px={24}
        py={{ base: 16, sm: 0 }}
        style={{
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Group gap='md'>
          <ActionIcon
            variant='subtle'
            onClick={() => navigate(-1)}
            size='lg'
            color={COLORS.LightGreen}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </ActionIcon>
          <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
            Design Request Details
          </Title>
        </Group>
      </Group>

      <Stack gap='xl' px={24} pb={24}>
        <Paper withBorder p='md' radius='md'>
          <Stack gap='xl'>
            <Group align='flex-start' gap='xl'>
              {request.advertisementUrl && (
                <AspectRatio ratio={16 / 9} style={{ position: 'relative' }}>
                  <Image
                    src={request.advertisementUrl}
                    alt={request.advertisementName}
                    width={175}
                    height={175}
                    radius='md'
                  />
                  <ActionIcon
                    variant='filled'
                    radius='md'
                    visibleFrom='sm'
                    onClick={() =>
                      request.advertisementUrl &&
                      setSelectedAd(request.advertisementUrl)
                    }
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faExpand}
                      style={{ fontSize: '14px', color: 'white' }}
                    />
                  </ActionIcon>
                </AspectRatio>
              )}
              <Group justify='space-between' align='flex-start' flex={1}>
                <Stack gap={4}>
                  <Group gap='xs'>
                    <Title order={3}>{request.advertisementName}</Title>
                    <AdStatusTooltip
                      status={request.status}
                      isHostMode={isHostMode}
                    >
                      <Paper
                        px='xs'
                        py={2}
                        radius='xl'
                        style={{
                          backgroundColor:
                            getStatusColor(request.status) + '20',
                        }}
                      >
                        <Text
                          size='xs'
                          fw={500}
                          c={getStatusColor(request.status)}
                        >
                          {getStatusText(request.status)}
                        </Text>
                      </Paper>
                    </AdStatusTooltip>
                  </Group>
                  <Group gap='xs'>
                    <Text c='dimmed'>
                      {request.locationName} - {request.deviceName}
                    </Text>
                  </Group>
                  {request.createdDate && (
                    <Text size='sm' c='dimmed'>
                      Submitted:{' '}
                      {convertServerDateToLocal(
                        request.createdDate,
                      )?.toLocaleString()}
                    </Text>
                  )}
                  <Text size='sm' c='dimmed'>
                    Design Notes: {request.designNotes}
                  </Text>
                  <Group>
                    <Text size='sm' c='dimmed'>
                      {request.adFiles.length} Ad Files{' '}
                    </Text>
                    <Button
                      size='xs'
                      onClick={() => downloadFiles(request.adFiles)}
                    >
                      Download Files
                    </Button>
                  </Group>
                </Stack>
                {request.status === AdStatuses.PendingAdvertiserApproval &&
                !isAdmin ? (
                  <Group>
                    <Text
                      component='button'
                      onClick={() => setShowChangesModal(true)}
                      c={COLORS.HiddenGemzRed}
                      style={{
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                    >
                      Request Changes
                    </Text>
                    <Button
                      color={COLORS.LightGreen}
                      onClick={handleApproveDesign}
                      loading={isApproving}
                      disabled={isApproving}
                      loaderProps={{ type: 'dots' }}
                    >
                      Approve Design
                    </Button>
                  </Group>
                ) : (
                  isAdmin &&
                  (request.status === AdStatuses.ChangesRequested ||
                    request.status === AdStatuses.PendingDesign) && (
                    <Stack gap='md' w={400}>
                      <Title order={4}>Upload Design</Title>
                      <Dropzone
                        radius='md'
                        h={300}
                        onDrop={async (files: File[]) => {
                          if (files.length > 0) {
                            await handleFileUpload(files[0])
                          }
                        }}
                        onReject={(files: any) =>
                          console.log('rejected files', files)
                        }
                        maxSize={5 * 1024 ** 2}
                        accept={[
                          'image/png',
                          'image/jpeg',
                          'image/jpg',
                          'image/gif',
                          'image/webp',
                          'image/heic',
                          'image/heif',
                        ]}
                        multiple={false}
                      >
                        <Group
                          justify='center'
                          gap='xl'
                          style={{ pointerEvents: 'none' }}
                        >
                          {isUploadingDesign ? (
                            <Loader />
                          ) : adFile != null ? (
                            <Stack>
                              <AspectRatio ratio={16 / 9}>
                                <Image src={adFile.url} radius='md'></Image>
                              </AspectRatio>
                              <FontAwesomeIcon
                                icon={faPencil}
                                style={{ alignSelf: 'end', paddingRight: 10 }}
                              />
                            </Stack>
                          ) : (
                            <AspectRatio ratio={16 / 9}>
                              <Group
                                gap={30}
                                h='100%'
                                justify='center'
                                align='center'
                                wrap='nowrap'
                              >
                                <FontAwesomeIcon icon={faImage} size='xl' />
                                <Stack>
                                  <Text size='xl' inline>
                                    Click here or drag file to add image
                                  </Text>
                                  <Text size='sm' inline lh={1.6}>
                                    All advertisements should be full HD
                                    resolution (1920x1080) with a 16:9 aspect
                                    ratio
                                  </Text>
                                </Stack>
                              </Group>
                            </AspectRatio>
                          )}
                        </Group>
                      </Dropzone>
                      {uploadedUrl && (
                        <Button
                          color={COLORS.LightGreen}
                          onClick={handleUpdateImageUrl}
                          loading={isSending}
                          disabled={isSending}
                          loaderProps={{ type: 'dots' }}
                        >
                          Send to Advertiser
                        </Button>
                      )}
                    </Stack>
                  )
                )}
              </Group>
            </Group>
          </Stack>
        </Paper>

        {request.messages && (
          <Stack gap='xs'>
            <Text fw={500}>Messages:</Text>
            {request.messages.map((message: DesignRequestMessage) => (
              <Paper
                key={message.id}
                p='md'
                withBorder
                style={{
                  backgroundColor:
                    message.userId === session?.user.id ? '#f8f9fa' : 'white',
                  borderLeft:
                    message.userId === session?.user.id
                      ? `4px solid ${COLORS.LightGreen}`
                      : '1px solid #dee2e6',
                }}
              >
                <Stack gap='xs'>
                  <Group justify='space-between'>
                    <Text
                      fw={500}
                      c={
                        message.userId === session?.user.id
                          ? COLORS.LightGreen
                          : undefined
                      }
                    >
                      {message.userName}
                    </Text>
                    <Text size='sm' c='dimmed'>
                      {convertServerDateToLocal(
                        message.createdDate,
                      )?.toLocaleString()}
                    </Text>
                  </Group>
                  <Text>{message.message}</Text>
                  {message.files && message.files.length > 0 && (
                    <Group gap='md' wrap='wrap'>
                      {message.files.map((file: FileWithUrl, index: number) => (
                        <div key={index} style={{ position: 'relative' }}>
                          <Image
                            src={file.url}
                            alt={file.fileName}
                            width={200}
                            height={200}
                            radius='md'
                            style={{ objectFit: 'cover' }}
                          />
                          <ActionIcon
                            variant='filled'
                            radius='md'
                            onClick={() => downloadFiles([file])}
                            style={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faDownload}
                              style={{ fontSize: '14px', color: 'white' }}
                            />
                          </ActionIcon>
                        </div>
                      ))}
                    </Group>
                  )}
                </Stack>
              </Paper>
            ))}

            <Paper withBorder p='sm' radius='md'>
              <Stack gap='md'>
                <TextInput
                  placeholder='Type your message...'
                  value={messageInput}
                  onChange={(event) =>
                    setMessageInput(event.currentTarget.value)
                  }
                />
                {attachedFiles.length > 0 && (
                  <Group>
                    {attachedFiles.map((file, index) => (
                      <Paper
                        key={index}
                        withBorder
                        p='xs'
                        radius='md'
                        style={{ position: 'relative' }}
                      >
                        <Group gap='xs'>
                          <FontAwesomeIcon
                            icon={faPaperclip}
                            color={COLORS.LightGreen}
                          />
                          <Text size='sm'>{file.name}</Text>
                          <Button
                            variant='subtle'
                            color='red'
                            size='xs'
                            onClick={() => handleRemoveFile(index)}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </Button>
                        </Group>
                      </Paper>
                    ))}
                  </Group>
                )}
                <Group justify='space-between'>
                  <FileButton
                    onChange={handleFileChange}
                    accept='image/*'
                    multiple
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant='subtle'
                        color={COLORS.LightGreen}
                        leftSection={<FontAwesomeIcon icon={faPaperclip} />}
                      >
                        Attach Files
                      </Button>
                    )}
                  </FileButton>
                  <Button
                    color={COLORS.LightGreen}
                    onClick={handleSendMessage}
                    disabled={!messageInput || isUploadingMessage}
                    loading={isUploadingMessage}
                  >
                    Send Message
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Stack>

      <Modal
        opened={!!selectedAd}
        onClose={() => setSelectedAd(null)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        >
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setSelectedAd(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
          {selectedAd && (
            <div
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'calc(90vh * 16/9)',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={selectedAd}
                alt={request.advertisementName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </Modal>

      <RequestChangesModal
        isOpen={showChangesModal}
        onClose={() => setShowChangesModal(false)}
        onConfirm={handleRequestChanges}
        isSubmitting={requestingChanges}
      />
    </Stack>
  )
}
